/**
 * Types for the internal admin notification delivery audit view.
 *
 * Backed by GET /api/v1/admin/notification-deliveries
 * (issue Djin-EMS-Software-Engineering/djin-cloud#265).
 *
 * The status enum mirrors the migration-029 CHECK constraint on
 * alarms.notification_deliveries.status — NOT the narrower three listed in the
 * issue body. Historical rows from Phases 6–7 still carry suppressed_stub.
 * `suppressed_opt_out` is migration 088 (djin-cloud#883).
 *
 * ⚠️ THIS LIST IS NOT THE FULL CONSTRAINT, and the header said "six values" as
 * though it were. Migration 046 added `queued`, `delivered` and `undelivered`
 * for the Twilio delivery callbacks and this union was never widened, so the
 * filter dropdown cannot select them. That gap is PRE-EXISTING and deliberately
 * left alone here rather than folded into a safety PR — filed separately. Do not
 * read this list as the authoritative vocabulary; the migrations are.
 */

export type DeliveryChannel = 'in_app' | 'email' | 'sms';

export type DeliveryStatus =
  | 'pending'
  | 'sent'
  | 'failed'
  | 'suppressed_stub'
  | 'suppressed_quiet_hours'
  | 'suppressed_dedup'
  | 'suppressed_opt_out';

export const DELIVERY_CHANNELS: DeliveryChannel[] = ['in_app', 'email', 'sms'];

export const DELIVERY_STATUSES: DeliveryStatus[] = [
  'pending',
  'sent',
  'failed',
  'suppressed_stub',
  'suppressed_quiet_hours',
  'suppressed_dedup',
  'suppressed_opt_out',
];

export interface NotificationDeliveryRow {
  delivery_id: string;
  ts: string;
  user_id: string;
  user_email: string | null;
  user_first_name: string | null;
  user_last_name: string | null;
  customer_id: string;
  /** Wire-format site_code (TEXT) — not the config.sites UUID */
  site_id: string;
  /** config.sites UUID — use for /project/:id navigation, not site_id (site_code) */
  site_uuid: string | null;
  site_name: string | null;
  alarm_event_id: string;
  device_id: string | null;
  condition_key: string | null;
  condition_label: string | null;
  severity: string | null;
  channel: DeliveryChannel;
  status: DeliveryStatus;
  provider_msg_id: string | null;
  error: string | null;
}

export interface NotificationDeliveriesFilters {
  page?: number;
  limit?: number;
  /** ISO 8601 timestamp */
  start_date?: string;
  /** ISO 8601 timestamp */
  end_date?: string;
  channel?: DeliveryChannel;
  status?: DeliveryStatus;
  user_id?: string;
  site_id?: string;
  /** UUID — only honored by the backend when caller is platform_admin */
  customer_id?: string;
}

export interface NotificationDeliveriesListResponse {
  success: boolean;
  data: {
    deliveries: NotificationDeliveryRow[];
    total: number;
    page: number;
    limit: number;
  };
}
